/**
 * Scrollable Candid patient-notes chat list for the RPA status modal.
 *
 * Rows come from `groupedByUseCase.note`. The bubble whose `id` matches
 * `metaData.rowData` is highlighted and centred after mount.
 *
 * @module candidRpaChatList
 */
import { ChatMessage } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { CandidNoteChatItem } from "../../../pages/worklists/singleOrder/candidUpload/types/candidNoteChatItem";
import { CandidWritebackRecord } from "../../../pages/worklists/singleOrder/candidUpload/types/candidWriteback";
import { CandidWritebacksByUseCase } from "../../../pages/worklists/singleOrder/candidUpload/utils/groupCandidWritebacksByUseCase";
import { RootState } from "../../../redux/store/store";
import { mapNoteWritebacksToChatItems } from "./mapNoteWritebacksToChatItems";

/** Class marking the highlighted chat message. */
const SELECTED_CHAT_CLASS = "selected";

/**
 * Reads note write-backs from modal metadata.
 *
 * @param state - Redux root state
 * @returns Note write-back rows, or an empty list
 */
const selectNoteWritebacks = (
  state: RootState,
): CandidWritebacksByUseCase["note"] => {
  const grouped = state.modal.metaData?.groupedByUseCase as
    CandidWritebacksByUseCase | undefined;
  return grouped?.note ?? [];
};

/**
 * Reads the write-back row that opened the modal, when present.
 *
 * @param state - Redux root state
 * @returns The clicked table row, or undefined
 */
const selectRowData = (state: RootState): CandidWritebackRecord | undefined =>
  (state.modal.metaData?.rowData as CandidWritebackRecord | undefined) ??
  undefined;

/**
 * Highlights the chat whose id matches the clicked row, if that row is a note.
 *
 * @param selectedRow - Write-back that opened the modal, if any
 * @param items - Mapped note chat rows
 * @returns Matching chat id, or an empty string
 */
const resolveSelectedChatId = (
  selectedRow: CandidWritebackRecord | undefined,
  items: CandidNoteChatItem[],
): string => {
  if (selectedRow?.id && items.some((item) => item.id === selectedRow.id)) {
    return selectedRow.id;
  }
  return "";
};

/**
 * Centres `selected` inside `container` by setting `scrollTop`.
 *
 * @param container - Overflow list that owns the scrollbar
 * @param selected - Highlighted message to bring into view
 */
const scrollSelectedIntoListCenter = (
  container: HTMLDivElement,
  selected: HTMLElement,
): void => {
  const containerTop = container.getBoundingClientRect().top;
  const selectedRect = selected.getBoundingClientRect();
  const offsetWithinList =
    selectedRect.top - containerTop + container.scrollTop;
  container.scrollTop =
    offsetWithinList - (container.clientHeight - selectedRect.height) / 2;
};

/**
 * Patient-notes chat list with selected-row highlight and on-mount scroll.
 *
 * Shows an empty-state message when `groupedByUseCase.note` has no
 * mappable payloads. Selection is skipped when `rowData.id` is not a
 * note write-back.
 *
 * @returns Scrollable chat list, or the empty-state panel
 */
const CandidRpaChatList = () => {
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const noteRecords = useSelector(selectNoteWritebacks);
  const selectedRow = useSelector(selectRowData);
  const chatItems = useMemo(
    () => mapNoteWritebacksToChatItems(noteRecords),
    [noteRecords],
  );
  const selectedChatId = useMemo(
    () => resolveSelectedChatId(selectedRow, chatItems),
    [selectedRow, chatItems],
  );

  /**
   * Centres the selected message after the dialog paints. `scrollTop` is
   * used instead of `scrollIntoView` so the page/dialog does not jump.
   */
  useEffect(() => {
    if (!selectedChatId) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      const container = chatListRef.current;
      const selected = container?.querySelector<HTMLElement>(
        `.${SELECTED_CHAT_CLASS}`,
      );
      if (!container || !selected) {
        return;
      }
      scrollSelectedIntoListCenter(container, selected);
    });

    return () => cancelAnimationFrame(frameId);
  }, [selectedChatId]);

  if (chatItems.length === 0) {
    return (
      <div className="candid-view-chats flex h-full min-h-0 items-center justify-center rounded-md border border-primaryGray-14 px-4 py-3 text-sm text-primaryGray-6">
        No notes available
      </div>
    );
  }

  return (
    <div
      ref={chatListRef}
      className="candid-view-chats flex h-full min-h-0 flex-col overflow-auto rounded-md border border-primaryGray-14 px-4 py-3"
    >
      {chatItems.map((item) => (
        <ChatMessage
          key={item.id}
          maxWidth="32rem"
          message={item.message}
          senderName={item.senderName}
          side="left"
          timestamp={item.timestamp}
          className={`candid-patient-notes ${item.id === selectedChatId ? SELECTED_CHAT_CLASS : ""}`}
        />
      ))}
    </div>
  );
};

export default CandidRpaChatList;
